import React, {FC, useEffect, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {DataEventActionsEnum} from '../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {OnboardingDataEventObject} from '../../../../libs/shared/core/src/models/tracking/objectNames/onboardingDataEventObject';
import {useDataEventTracking} from '../../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroup} from '../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroup';
import {StyledRequiredSpan} from '../../../../libs/shared/ui/src/components/fields/fieldLabel';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import channelToInboxToTeammates from '../../../assets/images/channelsToInboxToTeammates.svg';
import {ChallengesEnum, ContactMethodsEnum, PreferencesModel} from '../../../models/api/preferencesModel';
import {useOnboardingFlow} from '../flow/onboardingFlowHelpers';
import {OnboardingFlowTransitionEnum} from '../flow/onboardingFlowTypes';
import {OnboardingGuideAnimatedStyles, OnboardingGuidePopover} from '../guide/onboardingGuidePopover';
import {OnboardingGuideSlide} from '../guide/onboardingGuideSlide';
import {OnboardingSelectionButton} from './onboardingSelectionButton';
import {StyledOnboardingFormTitleDiv} from './onboardingWelcomeHelpers';

interface OnboardingQuestionnairePopoverProps {
  onClose: () => void;
  styles: OnboardingGuideAnimatedStyles | undefined;
  saveOnboardingQuestionnaireAnswers: (
    preferences: Pick<
      PreferencesModel['activationPreferences'],
      'questionnaireChallenges' | 'questionnaireContactMethods'
    >,
  ) => void;
  isQuestionnaireOpen: boolean;
}

const StyledPopoverContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const StyledQuestion = styled.div`
  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.small};
  margin-bottom: 12px;
  color: ${(p) => p.theme.greys.shade70};
`;

const StyledVisualContainerDiv = styled.div`
  flex: 0 0 50%;
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  background: ${(p) => p.theme.greys.shade20};
`;

const StyledOptionsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: start;
`;

const StyledOptionRow = styled.div`
  display: flex;
  gap: 12px;
`;

export const OnboardingQuestionnairePopover: FC<OnboardingQuestionnairePopoverProps> = (props) => {
  const {onClose, styles, saveOnboardingQuestionnaireAnswers, isQuestionnaireOpen} = props;

  const {trackDataEvent} = useDataEventTracking();

  const {navigateOnboardingFlow} = useOnboardingFlow();

  const [selectedChallenges, setSelectedChallenges] = useState<Array<ChallengesEnum>>([]);
  const [customChallenge, setCustomChallenge] = useState('');
  const [selectedContactMethods, setSelectedContactMethods] = useState<Array<ContactMethodsEnum>>([]);
  const [customContactMethod, setCustomContactMethod] = useState('');

  const handleChallengeToggle = (challenge: ChallengesEnum) => {
    setSelectedChallenges((prev) =>
      prev.includes(challenge) ? prev.filter((c) => c !== challenge) : [...prev, challenge],
    );
  };

  const handleContactMethodToggle = (method: ContactMethodsEnum) => {
    setSelectedContactMethods((prev) =>
      prev.includes(method) ? prev.filter((m) => m !== method) : [...prev, method],
    );
  };

  useEffect(() => {
    if (!isQuestionnaireOpen) {
      return;
    }
    trackDataEvent(DataEventActionsEnum.VIEW, OnboardingDataEventObject.ONBOARDING_QUESTIONNAIRE_VIEW);
  }, [isQuestionnaireOpen, trackDataEvent]);

  const handleStartSetup = async () => {
    const trimmedCustomChallenge = selectedChallenges.includes(ChallengesEnum.CUSTOM)
      ? customChallenge.trim()
      : null;
    const trimmedCustomContactMethod = selectedContactMethods.includes(ContactMethodsEnum.CUSTOM)
      ? customContactMethod.trim()
      : null;

    const answers = {
      questionnaireChallenges: {
        selected: selectedChallenges,
        custom: trimmedCustomChallenge,
      },
      questionnaireContactMethods: {
        selected: selectedContactMethods,
        custom: trimmedCustomContactMethod,
      },
    };

    trackDataEvent(
      DataEventActionsEnum.CLICK,
      OnboardingDataEventObject.ONBOARDING_QUESTIONNAIRE_START_SETUP,
      answers,
    );

    saveOnboardingQuestionnaireAnswers(answers);

    const isOnboarding = await navigateOnboardingFlow(
      OnboardingFlowTransitionEnum.ONBOARDING_QUESTIONNAIRE_COMPLETE,
    );

    if (!isOnboarding) {
      onClose();
    }
  };

  const handleSkip = () => {
    trackDataEvent(DataEventActionsEnum.CLICK, OnboardingDataEventObject.ONBOARDING_QUESTIONNAIRE_SKIP);

    navigateOnboardingFlow(OnboardingFlowTransitionEnum.ONBOARDING_QUESTIONNAIRE_CANCEL);

    onClose();
  };

  const isFormValid = selectedChallenges.length > 0 || selectedContactMethods.length > 0;

  return (
    <OnboardingGuidePopover styles={styles} onRequestClose={handleSkip}>
      <OnboardingGuideSlide
        navPrimary={
          <ButtonGroup>
            <Button variant={ButtonVariantsEnum.SECONDARY} onClick={handleSkip}>
              <Trans id="onboarding-questionnaire-skip">Skip</Trans>
            </Button>
            <Button variant={ButtonVariantsEnum.PRIMARY} onClick={handleStartSetup} isDisabled={!isFormValid}>
              <Trans id="onboarding-questionnaire-start-setup">Start setup</Trans>
            </Button>
          </ButtonGroup>
        }
        isLeftContent
      >
        <StyledPopoverContent>
          <StyledOnboardingFormTitleDiv>
            <Trans id="onboarding-questionnaire-title">Help us customize your experience</Trans>
          </StyledOnboardingFormTitleDiv>

          <div>
            <StyledQuestion>
              <Trans id="onboarding-questionnaire-challenge-question">
                What challenges are you looking to solve?<StyledRequiredSpan>*</StyledRequiredSpan>
              </Trans>
            </StyledQuestion>
            <StyledOptionsColumn>
              <OnboardingSelectionButton
                type="checkbox"
                isChecked={selectedChallenges.includes(ChallengesEnum.MULTI_CHANNEL_SUPPORT)}
                onClick={() => handleChallengeToggle(ChallengesEnum.MULTI_CHANNEL_SUPPORT)}
                label={
                  <Trans id="challenge-multi-channel-support">Deliver customer support across channels</Trans>
                }
              />
              <OnboardingSelectionButton
                type="checkbox"
                isChecked={selectedChallenges.includes(ChallengesEnum.SHARED_EMAIL_INBOXES)}
                onClick={() => handleChallengeToggle(ChallengesEnum.SHARED_EMAIL_INBOXES)}
                label={<Trans id="challenge-shared-email-inboxes">Manage shared email inboxes</Trans>}
              />

              <OnboardingSelectionButton
                type="checkbox"
                isChecked={selectedChallenges.includes(ChallengesEnum.CLIENT_SERVICE_AND_ACCOUNT_MANAGEMENT)}
                onClick={() => handleChallengeToggle(ChallengesEnum.CLIENT_SERVICE_AND_ACCOUNT_MANAGEMENT)}
                label={
                  <Trans id="challenge-client-service">
                    Streamline client service and account management
                  </Trans>
                }
              />
              <OnboardingSelectionButton
                type="checkbox"
                isChecked={selectedChallenges.includes(ChallengesEnum.INTERNAL_REQUESTS)}
                onClick={() => handleChallengeToggle(ChallengesEnum.INTERNAL_REQUESTS)}
                label={
                  <Trans id="challenge-internal-requests">Handle internal requests (HR, IT, etc.)</Trans>
                }
              />
              <OnboardingSelectionButton
                type="checkbox"
                isChecked={selectedChallenges.includes(ChallengesEnum.CUSTOM)}
                value={customChallenge}
                onChangeValue={setCustomChallenge}
                onClick={() => handleChallengeToggle(ChallengesEnum.CUSTOM)}
                label={<Trans id="challenge-custom">Something else</Trans>}
                isFreeform
                isFreeFormCompact
              />
            </StyledOptionsColumn>
          </div>

          <div>
            <StyledQuestion>
              <Trans id="onboarding-questionnaire-contact-question">
                How do your customers contact you today?<StyledRequiredSpan>*</StyledRequiredSpan>
              </Trans>
            </StyledQuestion>
            <StyledOptionsColumn>
              <StyledOptionRow>
                <OnboardingSelectionButton
                  type="checkbox"
                  isChecked={selectedContactMethods.includes(ContactMethodsEnum.EMAIL)}
                  onClick={() => handleContactMethodToggle(ContactMethodsEnum.EMAIL)}
                  label={<Trans id="contact-method-email">Email</Trans>}
                />
                <OnboardingSelectionButton
                  type="checkbox"
                  isChecked={selectedContactMethods.includes(ContactMethodsEnum.CHAT)}
                  onClick={() => handleContactMethodToggle(ContactMethodsEnum.CHAT)}
                  label={<Trans id="contact-method-chat">Chat</Trans>}
                />
                <OnboardingSelectionButton
                  type="checkbox"
                  isChecked={selectedContactMethods.includes(ContactMethodsEnum.VOICE)}
                  onClick={() => handleContactMethodToggle(ContactMethodsEnum.VOICE)}
                  label={<Trans id="contact-method-voice">Voice</Trans>}
                />
                <OnboardingSelectionButton
                  type="checkbox"
                  isChecked={selectedContactMethods.includes(ContactMethodsEnum.SMS)}
                  onClick={() => handleContactMethodToggle(ContactMethodsEnum.SMS)}
                  label={<Trans id="contact-method-sms">SMS</Trans>}
                />
              </StyledOptionRow>

              <StyledOptionRow>
                <OnboardingSelectionButton
                  type="checkbox"
                  isChecked={selectedContactMethods.includes(ContactMethodsEnum.SOCIAL_MEDIA)}
                  onClick={() => handleContactMethodToggle(ContactMethodsEnum.SOCIAL_MEDIA)}
                  label={<Trans id="contact-method-social-media">Social media</Trans>}
                />
                <OnboardingSelectionButton
                  type="checkbox"
                  isChecked={selectedContactMethods.includes(ContactMethodsEnum.WHATSAPP)}
                  onClick={() => handleContactMethodToggle(ContactMethodsEnum.WHATSAPP)}
                  label={<Trans id="contact-method-whatsapp">WhatsApp</Trans>}
                />
                <OnboardingSelectionButton
                  type="checkbox"
                  isChecked={selectedContactMethods.includes(ContactMethodsEnum.CUSTOM)}
                  value={customContactMethod}
                  onChangeValue={setCustomContactMethod}
                  onClick={() => handleContactMethodToggle(ContactMethodsEnum.CUSTOM)}
                  label={<Trans id="contact-method-custom">Something else</Trans>}
                  isFreeform
                  isFreeFormCompact
                />
              </StyledOptionRow>
            </StyledOptionsColumn>
          </div>
        </StyledPopoverContent>
      </OnboardingGuideSlide>

      <StyledVisualContainerDiv>
        <img
          src={channelToInboxToTeammates}
          alt="Channels to inbox to teammates illustration"
          height={172}
          width={415}
        />
      </StyledVisualContainerDiv>
    </OnboardingGuidePopover>
  );
};
